<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import InboxTemplatePermissionsAPI from 'dashboard/api/inboxTemplatePermissions';
import NextButton from 'dashboard/components-next/button/Button.vue';

// SICARG MOD 4 — Asignación de plantillas permitidas por inbox (admin).
const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const isSaving = ref(false);
const availableTemplates = ref([]);
const selectedNames = ref([]);
const query = ref('');

const filteredTemplates = computed(() =>
  availableTemplates.value.filter(tpl =>
    (tpl.name || '').toLowerCase().includes(query.value.toLowerCase())
  )
);

const allSelected = computed(
  () => availableTemplates.value.length > 0 && selectedNames.value.length === 0
);

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await InboxTemplatePermissionsAPI.get(props.inbox.id);
    availableTemplates.value = data.available_templates || [];
    selectedNames.value = data.allowed_template_names || [];
  } catch (error) {
    useAlert(t('INBOX_MGMT.INBOX_TEMPLATE_PERMISSIONS.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);

const isChecked = name => selectedNames.value.includes(name);

const toggle = name => {
  if (isChecked(name)) {
    selectedNames.value = selectedNames.value.filter(n => n !== name);
  } else {
    selectedNames.value = [...selectedNames.value, name];
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    const { data } = await InboxTemplatePermissionsAPI.update(
      props.inbox.id,
      selectedNames.value
    );
    selectedNames.value = data.allowed_template_names || [];
    // Refrescar el inbox en el store para que el filtro del selector se actualice
    await store.dispatch('inboxes/get');
    useAlert(t('INBOX_MGMT.INBOX_TEMPLATE_PERMISSIONS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.INBOX_TEMPLATE_PERMISSIONS.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 py-6">
    <div>
      <h4 class="text-base font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.INBOX_TEMPLATE_PERMISSIONS.TITLE') }}
      </h4>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.INBOX_TEMPLATE_PERMISSIONS.DESCRIPTION') }}
      </p>
      <p v-if="allSelected" class="mt-2 text-sm italic text-n-amber-11">
        {{ t('INBOX_MGMT.INBOX_TEMPLATE_PERMISSIONS.SHOWING_ALL') }}
      </p>
    </div>

    <input
      v-model="query"
      type="search"
      :placeholder="t('INBOX_MGMT.INBOX_TEMPLATE_PERMISSIONS.SEARCH')"
      class="w-full h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak"
    />

    <div v-if="isLoading" class="py-6 text-sm text-center text-n-slate-11">
      {{ t('INBOX_MGMT.INBOX_TEMPLATE_PERMISSIONS.LOADING') }}
    </div>

    <div
      v-else
      class="flex flex-col rounded-lg border divide-y border-n-weak divide-n-weak max-h-96 overflow-y-auto"
    >
      <label
        v-for="tpl in filteredTemplates"
        :key="tpl.id || tpl.name"
        class="flex gap-3 items-center p-3 cursor-pointer hover:bg-n-alpha-2"
      >
        <input
          type="checkbox"
          :checked="isChecked(tpl.name)"
          @change="toggle(tpl.name)"
        />
        <div class="flex-1">
          <p class="text-sm text-n-slate-12">{{ tpl.name }}</p>
          <p class="text-xs text-n-slate-10">
            {{ tpl.language }} · {{ tpl.category }} · {{ tpl.status }}
          </p>
        </div>
      </label>
      <p
        v-if="!filteredTemplates.length"
        class="p-4 text-sm text-center text-n-slate-10"
      >
        {{ t('INBOX_MGMT.INBOX_TEMPLATE_PERMISSIONS.EMPTY') }}
      </p>
    </div>

    <div>
      <NextButton blue :is-loading="isSaving" @click="save">
        {{ t('INBOX_MGMT.INBOX_TEMPLATE_PERMISSIONS.SAVE') }}
      </NextButton>
    </div>
  </div>
</template>
